import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { 
  Database, 
  CheckCircle, 
  AlertTriangle, 
  RefreshCw,
  Eye,
  Filter,
  Layers,
  Package,
  Crown,
  Settings
} from 'lucide-react';
import { gamesAPI } from '../../services/api';
import { 
  getSubscriptionTierName, 
  getSubscriptionTierBadgeColor,
  SUBSCRIPTION_TIERS,
  GAME_CATEGORIES 
} from '../../constants/games';
import LoadingSpinner from '../LoadingSpinner';
import toast from 'react-hot-toast';

const GameRegistryManagement = () => {
  const [selectedGame, setSelectedGame] = useState(null);
  const [activeTab, setActiveTab] = useState('registry');
  const [validationResult, setValidationResult] = useState(null);

  // Fetch game registry data
  const { data: registryData, isLoading: registryLoading, refetch: refetchRegistry } = useQuery(
    'gameRegistry',
    () => gamesAPI.getGameRegistry().then(res => res.data),
    {
      onSuccess: (data) => {
        console.log('Game registry loaded:', data);
      }
    }
  );

  // Fetch access matrix
  const { data: accessMatrix, isLoading: matrixLoading } = useQuery(
    'gameAccessMatrix',
    () => gamesAPI.getGameAccessMatrix().then(res => res.data),
    {
      enabled: activeTab === 'access'
    }
  );

  // Validate registry
  const { data: validation, isLoading: validationLoading, refetch: validateRegistry } = useQuery(
    'validateGameRegistry',
    () => gamesAPI.validateGameRegistry().then(res => res.data),
    {
      enabled: false, // Only run when manually triggered
      onSuccess: (data) => {
        setValidationResult(data);
        if (data.validation.isValid) {
          toast.success('Game registry validation passed!');
        } else {
          toast.error('Game registry validation failed!');
        }
      }
    }
  );

  const handleValidateRegistry = () => {
    validateRegistry();
  };

  const categoryNames = {
    [GAME_CATEGORIES.CLASSIC]: 'Classic',
    [GAME_CATEGORIES.PUZZLE]: 'Puzzle',
    [GAME_CATEGORIES.STRATEGY]: 'Strategy',
    [GAME_CATEGORIES.WORD]: 'Word',
    [GAME_CATEGORIES.MATH]: 'Math',
    [GAME_CATEGORIES.CARD]: 'Card'
  };

  if (registryLoading) {
    return <LoadingSpinner size="xl" />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-discord-lightest flex items-center">
              <Database className="h-6 w-6 mr-3" />
              Game Registry Management
            </h2>
            <p className="text-discord-light">
              Manage the centralized game registry and subscription access control
            </p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleValidateRegistry}
              disabled={validationLoading}
              className="btn btn-outline btn-sm"
            >
              {validationLoading ? (
                <LoadingSpinner size="sm" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Validate Registry
            </button>
            <button
              onClick={() => refetchRegistry()}
              className="btn btn-primary btn-sm"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tabs tabs-boxed">
        <button 
          className={`tab ${activeTab === 'registry' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('registry')}
        >
          <Layers className="h-4 w-4 mr-2" />
          Registry
        </button>
        <button 
          className={`tab ${activeTab === 'access' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('access')}
        >
          <Crown className="h-4 w-4 mr-2" />
          Access Matrix
        </button>
        <button 
          className={`tab ${activeTab === 'validation' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('validation')}
        >
          <AlertTriangle className="h-4 w-4 mr-2" />
          Validation
        </button>
      </div>

      {/* Registry Tab */}
      {activeTab === 'registry' && registryData && (
        <div className="space-y-6">
          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-discord-light">Total Games</p>
                  <p className="text-2xl font-bold text-discord-lightest">
                    {registryData.statistics.totalGames}
                  </p>
                </div>
                <Package className="h-8 w-8 text-blue-400" />
              </div>
            </div>
            
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-discord-light">Active Games</p>
                  <p className="text-2xl font-bold text-discord-lightest">
                    {registryData.statistics.activeGames}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
            </div>
            
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-discord-light">Free Games</p>
                  <p className="text-2xl font-bold text-discord-lightest">
                    {registryData.statistics.gamesByTier.free}
                  </p>
                </div>
                <Crown className="h-8 w-8 text-gray-400" />
              </div>
            </div>
            
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-discord-light">Pro Games</p>
                  <p className="text-2xl font-bold text-discord-lightest">
                    {registryData.statistics.gamesByTier.pro}
                  </p>
                </div>
                <Crown className="h-8 w-8 text-blue-400" />
              </div>
            </div>
          </div>

          {/* Games List */}
          <div className="card">
            <h3 className="text-lg font-bold text-discord-lightest mb-4">
              All Games in Registry
            </h3>
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>Game</th>
                    <th>Category</th>
                    <th>Tier</th>
                    <th>Status</th>
                    <th>Features</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {registryData.games.map((game) => (
                    <tr key={game.id}>
                      <td>
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl">{game.emoji}</span>
                          <div>
                            <div className="font-bold text-discord-lightest">
                              {game.name}
                            </div>
                            <div className="text-sm text-discord-light">
                              {game.description}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="badge badge-outline">
                          {categoryNames[game.category] || game.category}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${getSubscriptionTierBadgeColor(game.subscriptionTier)}`}>
                          {getSubscriptionTierName(game.subscriptionTier)}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${game.isActive ? 'badge-success' : 'badge-error'}`}>
                          {game.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {game.features.singlePlayer && (
                            <span className="badge badge-ghost badge-xs">Single</span>
                          )}
                          {game.features.multiplayer && (
                            <span className="badge badge-ghost badge-xs">Multi</span>
                          )}
                          {game.features.aiOpponent && (
                            <span className="badge badge-ghost badge-xs">AI</span>
                          )}
                          {game.features.statistics && (
                            <span className="badge badge-ghost badge-xs">Stats</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <button
                          onClick={() => setSelectedGame(game)}
                          className="btn btn-ghost btn-sm"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Access Matrix Tab */}
      {activeTab === 'access' && (
        <div className="space-y-6">
          {matrixLoading ? (
            <LoadingSpinner size="xl" />
          ) : (
            <div className="card">
              <h3 className="text-lg font-bold text-discord-lightest mb-4">
                Subscription Access Matrix
              </h3>
              <div className="overflow-x-auto">
                <table className="table table-zebra w-full">
                  <thead>
                    <tr>
                      <th>Subscription Tier</th>
                      <th>Available Games</th>
                      <th>Game Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accessMatrix?.accessMatrix?.map((tier) => (
                      <tr key={tier.tier}>
                        <td>
                          <div className="flex items-center space-x-2">
                            <span className={`badge ${getSubscriptionTierBadgeColor(tier.tier)}`}>
                              {getSubscriptionTierName(tier.tier)}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="flex flex-wrap gap-1">
                            {tier.games.map((gameId) => (
                              <span key={gameId} className="badge badge-outline badge-sm">
                                {gameId.replace('-', ' ')}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <span className="font-bold text-discord-lightest">
                            {tier.gameCount}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Validation Tab */}
      {activeTab === 'validation' && (
        <div className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-bold text-discord-lightest mb-4">
              Registry Validation
            </h3>
            
            {validationResult ? (
              <div className="space-y-4">
                <div className={`alert ${validationResult.validation.isValid ? 'alert-success' : 'alert-error'}`}>
                  <AlertTriangle className="h-6 w-6" />
                  <div>
                    <h4 className="font-bold">
                      {validationResult.validation.isValid ? 'Registry Valid' : 'Registry Invalid'}
                    </h4>
                    <p>
                      {validationResult.validation.isValid 
                        ? 'All games are properly configured and accessible.' 
                        : 'Found issues in the game registry configuration.'
                      }
                    </p>
                  </div>
                </div>

                {validationResult.validation.errors.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-bold text-red-400">Errors:</h4>
                    {validationResult.validation.errors.map((error, index) => (
                      <div key={index} className="alert alert-error">
                        <AlertTriangle className="h-4 w-4" />
                        <span>{error}</span>
                      </div>
                    ))}
                  </div>
                )}

                {validationResult.validation.warnings.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-bold text-yellow-400">Warnings:</h4>
                    {validationResult.validation.warnings.map((warning, index) => (
                      <div key={index} className="alert alert-warning">
                        <AlertTriangle className="h-4 w-4" />
                        <span>{warning}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Database className="h-12 w-12 text-discord-light mx-auto mb-4" />
                <p className="text-discord-light">
                  Click "Validate Registry" to check the game registry configuration
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Game Details Modal */}
      {selectedGame && (
        <div className="modal modal-open">
          <div className="modal-box max-w-4xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-discord-lightest">
                Game Details
              </h3>
              <button 
                className="btn btn-sm btn-circle btn-ghost"
                onClick={() => setSelectedGame(null)}
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <span className="text-4xl">{selectedGame.emoji}</span>
                <div>
                  <h4 className="text-xl font-bold text-discord-lightest">
                    {selectedGame.name}
                  </h4>
                  <p className="text-discord-light">{selectedGame.description}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h5 className="font-bold text-discord-lightest mb-2">Basic Info</h5>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-discord-light">Category:</span>
                      <span className="text-discord-lightest">{categoryNames[selectedGame.category]}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-discord-light">Tier:</span>
                      <span className={`badge ${getSubscriptionTierBadgeColor(selectedGame.subscriptionTier)}`}>
                        {getSubscriptionTierName(selectedGame.subscriptionTier)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-discord-light">Max Players:</span>
                      <span className="text-discord-lightest">{selectedGame.maxPlayers}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-discord-light">Time Limit:</span>
                      <span className="text-discord-lightest">{selectedGame.timeLimit}s</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h5 className="font-bold text-discord-lightest mb-2">Features</h5>
                  <div className="space-y-2">
                    {Object.entries(selectedGame.features).map(([feature, enabled]) => (
                      <div key={feature} className="flex justify-between">
                        <span className="text-discord-light">{feature}:</span>
                        <span className={`badge ${enabled ? 'badge-success' : 'badge-error'}`}>
                          {enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-action">
              <button 
                className="btn"
                onClick={() => setSelectedGame(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameRegistryManagement;
